import { formatShortDateWithWeekday } from "@clipboard-health/date-time";
import { Card, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { type ExternalAccountDetailsAttributes } from "@src/appV2/redesign/Accounts/PayoutSettings/api/schema";
import { parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";

import { type ShiftPaymentStatus } from "../../api/useGetShiftPaymentStatus";
import { type SupportShiftDetails } from "../../api/useShiftDetails";
import { type ScoreLedgerEntry } from "../../api/useShiftScoreLedger";
import { ClipboardScoreCard } from "./ClipboardScoreCard";
import { EarningsCard } from "./EarningsCard";
import { ShiftInfoRow } from "./ShiftInfoRow";
import { TimekeepingCard } from "./TimekeepingCard";

interface ShiftDetailsContentProps {
  readonly shiftDetails: SupportShiftDetails;
  readonly scoreLedger: ScoreLedgerEntry[];
  readonly isScoreLedgerLoading: boolean;
  readonly isPastShift: boolean;
  readonly payoutDestination: ExternalAccountDetailsAttributes | undefined;
  readonly paymentStatus: ShiftPaymentStatus | undefined;
  readonly isPaymentStatusLoading: boolean;
}

export function ShiftDetailsContent(props: ShiftDetailsContentProps) {
  const {
    shiftDetails,
    scoreLedger,
    isScoreLedgerLoading,
    isPastShift,
    payoutDestination,
    paymentStatus,
    isPaymentStatusLoading,
  } = props;
  const { workplaceTimezone } = shiftDetails;

  const startDate = parseISO(shiftDetails.startAt);
  const formattedDate = formatShortDateWithWeekday(startDate, { timeZone: workplaceTimezone });
  const scheduledTimeRange = `${formatTime(shiftDetails.startAt, workplaceTimezone)} - ${formatTime(
    shiftDetails.endAt,
    workplaceTimezone
  )}`;
  const hasClockData = isDefined(shiftDetails.clockInTime) || isDefined(shiftDetails.clockOutTime);
  const totalScoreChange = scoreLedger.reduce((sum, entry) => sum + entry.adjustment, 0);

  return (
    <Stack spacing={4}>
      <HeroCard
        formattedDate={formattedDate}
        qualificationName={shiftDetails.qualificationName}
        workplaceName={shiftDetails.workplaceName}
      />

      <ScheduledShiftCard
        scheduledTimeRange={scheduledTimeRange}
        workplaceAddress={shiftDetails.workplaceAddress}
      />

      {isPastShift && (
        <TimekeepingCard shiftDetails={shiftDetails} workplaceTimezone={workplaceTimezone} />
      )}

      {isPastShift && hasClockData && (
        <EarningsCard
          grossEarningsCents={shiftDetails.grossEarningsCents}
          hourlyRateCents={shiftDetails.hourlyRateCents}
          actualDurationHours={shiftDetails.actualDurationHours}
          isInstantPay={shiftDetails.isInstantPay}
          isVerified={shiftDetails.isVerified}
          payoutDestination={payoutDestination}
          paymentStatus={paymentStatus}
          isPaymentStatusLoading={isPaymentStatusLoading}
        />
      )}

      {isPastShift && (
        <ClipboardScoreCard
          isLoading={isScoreLedgerLoading}
          scoreLedger={scoreLedger}
          totalScoreChange={totalScoreChange}
        />
      )}
    </Stack>
  );
}

interface HeroCardProps {
  readonly workplaceName: string;
  readonly formattedDate: string;
  readonly qualificationName: string | undefined;
}

function HeroCard({ workplaceName, formattedDate, qualificationName }: HeroCardProps) {
  const subtitle = isDefined(qualificationName)
    ? `${formattedDate} · ${qualificationName}`
    : formattedDate;

  return (
    <Card variant="quaternary" sx={(theme) => ({ padding: theme.spacing(5) })}>
      <Stack spacing={1} alignItems="center">
        <Text semibold variant="h5" sx={{ textAlign: "center" }}>
          {workplaceName}
        </Text>
        <Text
          variant="body2"
          sx={(theme) => ({ color: theme.palette.text.secondary, textAlign: "center" })}
        >
          {subtitle}
        </Text>
      </Stack>
    </Card>
  );
}

interface ScheduledShiftCardProps {
  readonly scheduledTimeRange: string;
  readonly workplaceAddress: string | undefined;
}

function ScheduledShiftCard(props: ScheduledShiftCardProps) {
  const { scheduledTimeRange, workplaceAddress } = props;

  return (
    <Card variant="tertiary" sx={(theme) => ({ padding: theme.spacing(5) })}>
      <Stack spacing={3}>
        <Text semibold variant="body1">
          Scheduled Shift
        </Text>
        <ShiftInfoRow label="Time" value={scheduledTimeRange} />
        {isDefined(workplaceAddress) && <ShiftInfoRow label="Location" value={workplaceAddress} />}
      </Stack>
    </Card>
  );
}

function formatTime(isoString: string, timezone: string): string {
  return formatInTimeZone(parseISO(isoString), timezone, "h:mm a");
}
